import {
    AfterViewChecked,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    HostBinding,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Fixture } from '@cds/betting-offer';
import { EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { VirtualCompetitionPrefix } from '@frontend/sports/common/core/data-access/sport-model';
import { interpolateSingleBracesString } from '@frontend/sports/common/core/utils/string';
import { SportUrlParam, UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { ContentLink, MediaQueryService } from '@frontend/vanilla/core';
import { times } from 'lodash-es';
import { BehaviorSubject, noop } from 'rxjs';

import { ColumnLayoutService } from '../../layout/column-layout.service';
import { ColumnSizeData } from '../../layout/layout.model';
import { TopEventsMappingService } from '../top-events-mapping.service';
import { TopEventsTrackingService } from '../top-events-tracking.service';
import { EventDataForOptionPanel, PodType, TopEventsPodData } from '../top-events.model';

@Component({
    selector: 'ms-top-events-props-multipod',
    templateUrl: 'top-events-props-multipod.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class TopEventsPropsMultiPodComponent implements OnInit, OnChanges, OnDestroy, AfterViewChecked {
    @HostBinding('class') className = 'props-multi-pod';
    @HostBinding('class.pod-header-hidden') get podHeaderHidden() {
        return this.pod?.hidePodHeader;
    }

    @Input() pod: TopEventsPodData;
    @Input() index: number;

    vms$ = new BehaviorSubject<EventDataForOptionPanel[]>([]);

    link: ContentLink;
    limit: number;
    columns$ = new BehaviorSubject<number[]>([]);

    tracking = `TopEvents|${PodType.PropsMultiPod}`;
    trackingModuleType = PodType.PropsMultiPod;
    numberOfColumns = 1;
    contentRendered = false;

    private readonly TWO_COLUMNS_MIN_WIDTH = 600;
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        public sitecore: Sitecore,
        private topEventsPodService: TopEventsMappingService,
        private urlHelper: UrlHelperService,
        private topEventsTrackingService: TopEventsTrackingService,
        private mediaService: MediaQueryService,
        private columnLayoutService: ColumnLayoutService,
        private changeDetectionRef: ChangeDetectorRef,
    ) {}

    ngAfterViewChecked(): void {
        if (!this.contentRendered) {
            this.contentRendered = true;
        }
    }

    ngOnInit(): void {
        this.onColumnSizeChanged(this.columnLayoutService.currentCenterColumnSize);
        this.limit = this.pod.eventsBeforeShowMore ? this.pod.eventsBeforeShowMore * this.numberOfColumns : this.pod.events.length;

        this.columnLayoutService.centerColumnSizeChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(this.onColumnSizeChanged);
    }

    ngOnChanges(): void {
        this.pod.updateHandler = this.updateViewModel;

        if (this.pod.events?.length) {
            this.updateViewModel();

            this.link = this.pod.link ?? this.getLink(this.pod.fixtures, this.pod.linkDefaultText);
        }
    }

    private updateViewModel = (): void => {
        const vms = this.pod.events.map((e) =>
            this.topEventsPodService.getPodEventData({
                eventData: e,
                sourceFixtures: this.pod.sourceFixtures,
                selectionTextOverride: undefined,
                isDetailed: true,
            }),
        );

        this.vms$.next(vms);
    };

    private onColumnSizeChanged = (column: ColumnSizeData): void => {
        const centerColumnSize = column.width;
        this.numberOfColumns =
            this.pod.events.length &&
            this.pod.events.length > 1 &&
            centerColumnSize > this.TWO_COLUMNS_MIN_WIDTH &&
            ((this.mediaService.isActive('gt-xs') && !this.mediaService.isActive('xs-height')) || this.mediaService.isActive('gt-sm'))
                ? 2
                : 1;

        this.columns$.next(times(this.numberOfColumns, (index) => index));
    };

    trackByEventId(_: number, vm: EventDataForOptionPanel): string {
        return vm.eventModel.id;
    }

    trackByOptionGroupId(_: number, og: EventOptionGroup): string {
        return og.id;
    }

    private getLink(fixtures: Fixture[], defaultText: string | undefined): ContentLink {
        const isSameCompetition = new Set(fixtures.map((f) => f.competition.parentLeagueId ?? f.competition.id)).size === 1;
        const fixture = fixtures[0];
        const sport = {
            id: fixture.sport.id,
            name: fixture.sport.name.value,
        };

        let url: string;

        if (isSameCompetition) {
            const region = {
                id: fixture.region.id,
                name: fixture.region.name.value,
            };

            const isVirtualCompetition = !!fixture.virtualCompetition;
            const competition = {
                id: isVirtualCompetition ? fixture.virtualCompetition.id : (fixture.competition.parentLeagueId ?? fixture.competition.id),
                name: fixture.competition.name.value,
                prefix: isVirtualCompetition ? VirtualCompetitionPrefix : undefined,
            };

            url = this.urlHelper.getSportUrl(sport, SportUrlParam.Betting, region, competition);
        } else {
            url = this.urlHelper.getSportUrl(sport, SportUrlParam.Betting);
        }

        const text =
            interpolateSingleBracesString(defaultText, {
                target: isSameCompetition ? fixture.competition.name.value : fixture.sport.name.value,
            }) || this.sitecore.event.ShowMore;

        return { url, text };
    }

    showHideEvents(isExpanded: boolean): void {
        if (this.pod.eventsBeforeShowMore) {
            this.limit = this.limit < this.vms$.value.length ? this.vms$.value.length : this.pod.eventsBeforeShowMore * this.numberOfColumns;
        } else {
            this.limit = this.vms$.value.length;
        }

        this.topEventsTrackingService.trackShowMoreLess(this.pod, this.index, isExpanded);
        this.changeDetectionRef.detectChanges();
    }

    trackEventHeaderClick(): void {
        this.topEventsTrackingService.trackEventHeaderClick(this.pod, this.index);
    }

    trackBottomLink(): void {
        this.topEventsTrackingService.trackBottomLink(this.pod, this.index);
    }

    ngOnDestroy(): void {
        this.pod.updateHandler = noop;
    }
}
